/**
 * useDashboardData — extracted from DashboardIndicators.jsx
 *
 * Owns all data-fetching, state, filters, card-order logic, and data-mapping
 * helpers for the /dashboard indicators view.
 *
 * The component keeps only UI-specific state (tooltip, modal, tutorial,
 * report generation) and the JSX rendering.
 */
import {
    useState,
    useCallback,
    useRef,
    useMemo,
    useEffect,
} from 'react';
import moment from 'moment';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
    API_BASE_URL_DASHBOARD,
    API_BASE_URL,
} from '../utils/config';
import { useAuth } from '../context/AuthContext';
import analysisConfig from '../utils/analysisConfig';

/* ─────────────── module-level constants ─────────────── */

const VARIOS_CARD_IDS = [
    'totalHoras', 'totalAltura', 'totalPorcentajeVariacion',
    'totalVelocidad', 'totalHumedad',
    'promedioHoras', 'promedioAltura', 'promedioPorcentajeVariacion',
    'promedioVelocidad', 'promedioHumedad',
    'promedioVolumenAgua', 'totalVolumenAgua',
    'promedioDosisProducto', 'totalDosisProducto',
    'promedioVolumenMezcla', 'totalVolumenMezcla',
];

const ORDER_CACHE_PREFIX = 'DashboardIndicators:cardOrder:v1';

export const AREA_INDICATORS = {
    promedio: {
        donut: [
            'promedioAreaAplicada',
            'promedioAreaSobreAplicada',
            'promedioAreaNoAplicada',
        ],
        bar: [
            'promedioAreaAplicadaFueraDelLote',
            'promedioAreaAplicadaEnCalles',
        ],
    },
    total: {
        donut: [
            'totalAreaAplicada',
            'totalAreaSobreAplicada',
            'totalAreaNoAplicada',
        ],
        bar: [
            'totalAreaAplicadaFueraDelLote',
            'totalAreaAplicadaEnCalles',
        ],
    },
};

const AA_DOSIS_DATA_CARD_IDS = [
    'promedioDosisTeorica',
    'promedioDosisRealTotal',
    'diferenciaDosis',
    'promedioPorcentajeVariacionDosis',
];

const AA_INDICADORES_VARIOS_DATA_CARD_IDS = [
    'promedioAltura',
    'promedioVelocidad',
    'totalHoras',
    'promedioHumedad',
    'promedioTch',
    'totalVolumenMezcla',
    'promedioVolumenMezcla',
    'totalVolumenAgua',
    'promedioVolumenAgua',
    'totalDosisProducto',
    'promedioDosisProducto',
    'promedioPorcentajeVariacion',
];

const AA_DATA_CARD_DEFINITIONS = {
    promedioDosisTeorica: { id: 'promedioDosisTeorica', title: 'Promedio de volumen teórico', type: 'dataCard', limitName: 'dosisTeorica', dimensional: 'L/ha' },
    promedioDosisRealTotal: { id: 'promedioDosisRealTotal', title: 'Promedio de volumen real', type: 'dataCard', limitName: 'dosisRealTotal', dimensional: 'L/ha' },
    diferenciaDosis: { id: 'diferenciaDosis', title: 'Diferencia de volumen teórico vs real', type: 'dataCard', limitName: 'dosisRealTeorica', dimensional: 'L/ha' },
    promedioPorcentajeVariacionDosis: { id: 'promedioPorcentajeVariacionDosis', title: 'Porcentaje de variación de dosis', type: 'dataCard', limitName: 'porcentajeVariacionDosis', dimensional: '%' },
    promedioAltura: { id: 'promedioAltura', title: 'Altura', type: 'dataCard', limitName: 'altura', dimensional: 'm' },
    promedioVelocidad: { id: 'promedioVelocidad', title: 'Velocidad', type: 'dataCard', limitName: 'velocidad', dimensional: 'm/s' },
    totalHoras: { id: 'totalHoras', title: 'Horas', type: 'dataCard', limitName: 'totalHoras', dimensional: 'h' },
    promedioHumedad: { id: 'promedioHumedad', title: 'Humedad del cultivo', type: 'dataCard', limitName: 'humedadDelCultivo', dimensional: '%' },
    promedioTch: { id: 'promedioTch', title: 'TCH', type: 'dataCard', limitName: 'tch', dimensional: 't/ha' },
    totalVolumenMezcla: { id: 'totalVolumenMezcla', title: 'Total Volumen Mezcla', type: 'dataCard', limitName: 'totalVolumenMezcla', dimensional: 'L' },
    promedioVolumenMezcla: { id: 'promedioVolumenMezcla', title: 'Promedio Volumen Mezcla', type: 'dataCard', limitName: 'promedioVolumenMezcla', dimensional: 'L' },
    totalVolumenAgua: { id: 'totalVolumenAgua', title: 'Total Volumen de Agua', type: 'dataCard', limitName: 'totalVolumenAgua', dimensional: 'L' },
    promedioVolumenAgua: { id: 'promedioVolumenAgua', title: 'Promedio Volumen de Agua', type: 'dataCard', limitName: 'promedioVolumenAgua', dimensional: 'L' },
    totalDosisProducto: { id: 'totalDosisProducto', title: 'Total Dosis de Producto', type: 'dataCard', limitName: 'totalDosisProducto', dimensional: 'L/ha' },
    promedioDosisProducto: { id: 'promedioDosisProducto', title: 'Promedio Dosis de Producto', type: 'dataCard', limitName: 'promedioDosisProducto', dimensional: 'L/ha' },
    promedioPorcentajeVariacion: { id: 'promedioPorcentajeVariacion', title: 'Porcentaje de Variación de Áreas', type: 'dataCard', limitName: 'porcentajeVariacion', dimensional: '%' },
};

const AA_LEGACY_DATA_CARD_REPLACEMENTS = {
    sumaDosisReal: 'promedioPorcentajeVariacionDosis',
};

const AA_REMOVED_DATA_CARD_IDS = new Set([]);

const normalizeAADataCardsOrder = (cardsArray = []) => {
    if (!Array.isArray(cardsArray) || cardsArray.length === 0) return [];

    const desiredIds = [...AA_DOSIS_DATA_CARD_IDS, ...AA_INDICADORES_VARIOS_DATA_CARD_IDS];
    const desiredIdsSet = new Set(desiredIds);
    const originalIds = new Set(cardsArray.map((card) => card?.id).filter(Boolean));
    const needsMigration =
        Object.keys(AA_LEGACY_DATA_CARD_REPLACEMENTS).some((id) => originalIds.has(id)) ||
        [...AA_REMOVED_DATA_CARD_IDS].some((id) => originalIds.has(id)) ||
        desiredIds.some((id) => !originalIds.has(id));

    const normalizedCards = [];
    const cardsByDesiredId = new Map();
    const seenIds = new Set();
    let insertionIndex = -1;

    cardsArray.forEach((card) => {
        if (!card?.id) return;

        const replacementId = AA_LEGACY_DATA_CARD_REPLACEMENTS[card.id];
        const nextId = replacementId || card.id;

        if (AA_REMOVED_DATA_CARD_IDS.has(card.id) || AA_REMOVED_DATA_CARD_IDS.has(nextId)) {
            if (insertionIndex < 0) insertionIndex = normalizedCards.length;
            return;
        }

        const definition = AA_DATA_CARD_DEFINITIONS[nextId];
        const normalizedCard = definition ? { ...card, ...definition, id: nextId } : { ...card, id: nextId };

        if (needsMigration && (desiredIdsSet.has(nextId) || replacementId)) {
            if (insertionIndex < 0) insertionIndex = normalizedCards.length;
            cardsByDesiredId.set(nextId, normalizedCard);
            return;
        }

        if (!seenIds.has(nextId)) {
            normalizedCards.push(normalizedCard);
            seenIds.add(nextId);
        }
    });

    if (!needsMigration) return normalizedCards;

    const desiredCards = desiredIds.map((id) => {
        const savedCard = cardsByDesiredId.get(id);
        return savedCard
            ? { ...savedCard, ...AA_DATA_CARD_DEFINITIONS[id] }
            : { ...AA_DATA_CARD_DEFINITIONS[id] };
    });
    const desiredCardsSet = new Set(desiredCards.map((card) => card.id));
    const remainingCards = normalizedCards.filter((card) => !desiredCardsSet.has(card.id));
    const targetIndex = insertionIndex < 0 ? remainingCards.length : Math.min(insertionIndex, remainingCards.length);

    return [
        ...remainingCards.slice(0, targetIndex),
        ...desiredCards,
        ...remainingCards.slice(targetIndex),
    ];
};

const AA_PER_VUELO_IDS = new Set(['alturasPorVuelo', 'litrosPorVuelo', 'litrosHaPorVuelo']);

const getCardOrderReadyKey = (userId, activity) => `${userId}|${activity}`;

/* ── One-time default visibility migration (v1) ── */
const AA_HIDDEN_MIGRATION_VERSION = 1;

// Chart cards for these entity sections are hidden in full
const AA_HIDDEN_ENTITY_SECTIONS = ['Producto', 'Zafra', 'Lote', 'Tercio'];

// Specific IDs to hide (partial sections: Finca + Operador)
const AA_HIDDEN_SPECIFIC_IDS = new Set([
    // Totales por Finca
    'totalVolumenAguaFinca', 'totalDosisProductoFinca', 'totalVolumenMezclaFinca',
    // Promedios por Finca
    'promedioTchFinca', 'promedioVolumenAguaFinca', 'promedioDosisProductoFinca', 'promedioVolumenMezclaFinca',
    // Totales por Operador
    'totalAreaAplicadaEnCallesOperador', 'totalAreaAplicadaFueraDelLoteOperador', 'totalDosisRealOperador',
    'totalVolumenAguaOperador', 'totalDosisProductoOperador', 'totalVolumenMezclaOperador',
    // Promedios por Operador
    'promedioTchOperador', 'promedioVolumenAguaOperador', 'promedioDosisProductoOperador', 'promedioVolumenMezclaOperador',
]);

const isAACardHiddenByDefault = (cardId) => {
    if (!cardId) return false;
    if (AA_HIDDEN_SPECIFIC_IDS.has(cardId)) return true;
    return AA_HIDDEN_ENTITY_SECTIONS.some((ent) => cardId.includes(ent));
};

const normalizeOrderForActivity = (activity, orderObj) => {
    if (activity !== 'APLICACIONES_AEREAS' || !orderObj) return orderObj;

    // Normalize legacy IDs with spaces (e.g. 'promedioVolumen AguaFinca' → 'promedioVolumenAguaFinca')
    const stripSpaces = (id) => (typeof id === 'string' ? id.replace(/ /g, '') : id);

    let chartCards = Array.isArray(orderObj.chartCards)
        ? orderObj.chartCards
            .filter((c) => !AA_PER_VUELO_IDS.has(c?.id))
            .map((c) => (c?.id?.includes(' ') ? { ...c, id: stripSpaces(c.id) } : c))
        : (orderObj.chartCards ?? []);

    let hiddenCardIds = Array.isArray(orderObj.hiddenCardIds)
        ? orderObj.hiddenCardIds
            .filter((id) => !AA_PER_VUELO_IDS.has(id))
            .map((id) => (typeof id === 'string' && id.includes(' ') ? stripSpaces(id) : id))
        : [];

    // Apply one-time migration: inject default hidden IDs for all existing users
    const migrationDone = (orderObj._aaHiddenV ?? 0) >= AA_HIDDEN_MIGRATION_VERSION;
    if (!migrationDone) {
        const hiddenSet = new Set(hiddenCardIds);
        chartCards.forEach((c) => {
            if (c?.id && isAACardHiddenByDefault(c.id)) hiddenSet.add(c.id);
        });
        hiddenCardIds = Array.from(hiddenSet);
    }

    return {
        ...orderObj,
        dataCards: normalizeAADataCardsOrder(orderObj.dataCards),
        chartCards,
        hiddenCardIds,
        _aaHiddenV: AA_HIDDEN_MIGRATION_VERSION,
    };
};

const round2 = (n) => Number(Number.parseFloat(n ?? 0).toFixed(2));

/* ═══════════════════════════════════════════════════════ */
/*                       HOOK                             */
/* ═══════════════════════════════════════════════════════ */

export default function useDashboardData() {
    /* ──── auth ──── */
    const { userData, userId: loggedUserId, isViewer, isAnalyst } = useAuth();

    const safeLoggedUserId =
        loggedUserId ??
        userData?.userId ??
        userData?.ID_USUARIO ??
        userData?._id ??
        null;

    const canEdit = !isViewer;

    const effectiveUserId = useMemo(() => {
        if (!safeLoggedUserId) return null;
        return safeLoggedUserId;
    }, [safeLoggedUserId]);

    /* ──── activity ──── */
    const [selectedActivity, setSelectedActivity] = useState('COSECHA_MECANICA');
    const selectedActivityRef = useRef('COSECHA_MECANICA');
    useEffect(() => {
        selectedActivityRef.current = selectedActivity;
    }, [selectedActivity]);

    const [shouldFetch, setShouldFetch] = useState(true);
    const [fetchTriggerVersion, setFetchTriggerVersion] = useState(0);
    const handledFetchTriggerRef = useRef(-1);
    const fetchReasonRef = useRef('init');
    const lastIndicatorsFetchRef = useRef({});
    const abortControllerRef = useRef(null);
    const totalAnalisisAbortRef = useRef(null);

    /* ──── filter options ──── */
    const [filterOptions, setFilterOptions] = useState([]);

    const [filtersCM, setFiltersCM] = useState({
        loteOptions: [],
        operadorOptions: [],
        fincasOptions: [],
        zafrasOptions: [],
        tercioOptions: [],
    });

    const [filtersAA, setFiltersAA] = useState({
        operadorOptionsAA: [],
        fincasOptionsAA: [],
        productosOptionsAA: [],
        zafraOptionsAA: [],
        loteOptionsAA: [],
        tercioOptionsAA: [],
    });

    const [filtersFR, setFiltersFR] = useState({
        operadorOptionsFR: [],
        fincaOptionsFR: [],
        productosOptionsFR: [],
        zafraOptionsFR: [],
        tercioOptionsFR: [],
    });

    const [filtersDM, setFiltersDM] = useState({
        loteOptionsDM: [],
        fincasOptionsDM: [],
        zafraOptionsDM: [],
        tercioOptionsDM: [],
    });

    const [companyUsers, setCompanyUsers] = useState([]);
    const companyUserEmailToId = useMemo(
        () => Object.fromEntries(companyUsers.map((u) => [u.EMAIL, u._id])),
        [companyUsers]
    );

    const [filtersFetched, setFiltersFetched] = useState({
        COSECHA_MECANICA: false,
        APLICACIONES_AEREAS: false,
        FERTILIZACION: false,
        DETECCION_MALEZA: false,
    });

    const [selectedFilters, setSelectedFilters] = useState({});

    const [dateRange, setDateRange] = useState({
        startDate: moment().startOf('month'),
        endDate: moment().endOf('month'),
    });

    /* ──── loading state ──── */
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showGlobalLoader, setShowGlobalLoader] = useState(false);
    const [isOrderLoading, setIsOrderLoading] = useState(false);

    useEffect(() => {
        const busy = loading || isOrderLoading;
        if (!busy) {
            setShowGlobalLoader(false);
            return;
        }
        const t = setTimeout(() => setShowGlobalLoader(true), 450);
        return () => clearTimeout(t);
    }, [loading, isOrderLoading]);

    /* ──── card order state ──── */
    const [dataCardsOrder, setDataCardsOrder] = useState([]);
    const [chartCardsOrder, setChartCardsOrder] = useState([]);
    const chartCardsOrderRef = useRef([]);
    const dataCardsOrderRef = useRef([]);
    const [orderActivity, setOrderActivity] = useState(null);
    const [loadedOrderKey, setLoadedOrderKey] = useState(null);
    const orderCacheRef = useRef({});
    const orderFetchSeqRef = useRef(0);

    /* ──── card visibility state ──── */
    const [hiddenCards, setHiddenCards] = useState({});
    const hiddenCardsRef = useRef({});

    /* ──── DETECCION_MALEZA data ──── */
    const [totalAreaMaleza, setTotalAreaMaleza] = useState([]);
    const [totalAreaPoligono, setTotalAreaPoligono] = useState([]);
    const [totalPercentageMaleza, setTotalPercentageMaleza] = useState([]);
    const [metricasFincaDM, setMetricasFincaDM] = useState([]);
    const [totalesIndicadoresFincaDM, setTotalesIndicadoresFincaDM] = useState([]);
    const [metricasZafraDM, setMetricasZafraDM] = useState([]);
    const [totalesIndicadoresZafraDM, setTotalesIndicadoresZafraDM] = useState([]);
    const [metricasTercioDM, setMetricasTercioDM] = useState([]);
    const [totalesIndicadoresTercioDM, setTotalesIndicadoresTercioDM] = useState([]);
    const [lotesDMData, setLotesDMData] = useState([]);

    /* ──── COSECHA_MECANICA data ──── */
    const [promedioIndicadores, setPromedioIndicadores] = useState([]);
    const [porcentajes, setPorcentajes] = useState([]);
    const [metricasMaquina, setMetricasMaquina] = useState([]);
    const [porcentajesMaquina, setPorcentajesMaquina] = useState([]);
    const [metricasOperadorCM, setMetricasOperadorCM] = useState([]);
    const [porcentajesOperadorCM, setPorcentajesOperadorCM] = useState([]);
    const [metricasFinca, setMetricasFinca] = useState([]);
    const [porcentajesFinca, setPorcentajesFinca] = useState([]);

    /* ──── APLICACIONES_AEREAS / FERTILIZACION data ──── */
    const [tiempoTotal, setTiempoTotal] = useState([]);
    const [dosisTeoricaReal, setDosisTeoricaReal] = useState([]);
    const [totalesIndicadores, setTotalesIndicadores] = useState([]);
    const [totalesIndicadoresFinca, setTotalesIndicadoresFinca] = useState([]);
    const [totalesIndicadoresProducto, setTotalesIndicadoresProducto] = useState([]);
    const [metricasProducto, setMetricasProducto] = useState([]);
    const [totalesIndicadoresOperador, setTotalesIndicadoresOperador] = useState([]);
    const [metricasOperador, setMetricasOperador] = useState([]);
    const [totalesIndicadoresZafra, setTotalesIndicadoresZafra] = useState([]);
    const [metricasZafra, setMetricasZafra] = useState([]);
    const [totalesIndicadoresLote, setTotalesIndicadoresLote] = useState([]);
    const [metricasLote, setMetricasLote] = useState([]);
    const [totalesIndicadoresTercio, setTotalesIndicadoresTercio] = useState([]);
    const [metricasTercio, setMetricasTercio] = useState([]);
    const [metricasVuelo, setMetricasVuelo] = useState(null);
    // Bloque adicional del Dashboard Indicadores, exclusivo para VRA.
    const [vraResumen, setVraResumen] = useState(null);
    const [vraMetricasDimensiones, setVraMetricasDimensiones] = useState({});

    /* ──── misc data ──── */
    const [limites, setLimites] = useState({});
    const [totalAnalisisCount, setTotalAnalisisCount] = useState(0);

    /* ═══════════════════════════════════════════════════ */
    /*           RESET INDICATORS                         */
    /* ═══════════════════════════════════════════════════ */

    const resetIndicatorsData = useCallback(() => {
        // DETECCION_MALEZA
        setTotalAreaMaleza([]);
        setTotalAreaPoligono([]);
        setTotalPercentageMaleza([]);
        setMetricasFincaDM([]);
        setTotalesIndicadoresFincaDM([]);
        setMetricasZafraDM([]);
        setTotalesIndicadoresZafraDM([]);
        setMetricasTercioDM([]);
        setTotalesIndicadoresTercioDM([]);
        setLotesDMData([]);

        // COSECHA_MECANICA
        setPromedioIndicadores([]);
        setPorcentajes([]);
        setMetricasMaquina([]);
        setPorcentajesMaquina([]);
        setMetricasOperadorCM([]);
        setPorcentajesOperadorCM([]);
        setMetricasFinca([]);
        setPorcentajesFinca([]);

        // APLICACIONES_AEREAS / FERTILIZACION
        setTiempoTotal([]);
        setDosisTeoricaReal([]);
        setTotalesIndicadores([]);
        setTotalesIndicadoresFinca([]);
        setTotalesIndicadoresProducto([]);
        setMetricasProducto([]);
        setTotalesIndicadoresOperador([]);
        setMetricasOperador([]);
        setTotalesIndicadoresZafra([]);
        setMetricasZafra([]);
        setTotalesIndicadoresLote([]);
        setMetricasLote([]);
        setTotalesIndicadoresTercio([]);
        setMetricasTercio([]);
        setMetricasVuelo(null);
        setVraResumen(null);
        setVraMetricasDimensiones({});

        // Extras
        setLimites({});
        setTotalAnalisisCount(0);
    }, []);

    /* ═══════════════════════════════════════════════════ */
    /*           CARD ORDER – cache helpers                */
    /* ═══════════════════════════════════════════════════ */

    const getOrderCacheKey = (userId, activity) => `${ORDER_CACHE_PREFIX}:${userId}:${activity}`;

    const readOrderCache = useCallback((userId, activity) => {
        try {
            if (typeof window === 'undefined' || !window.localStorage) return null;
            const raw = window.localStorage.getItem(getOrderCacheKey(userId, activity));
            if (!raw) return null;

            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object') return null;

            const dataCards = Array.isArray(parsed.dataCards) ? parsed.dataCards : [];
            const chartCards = Array.isArray(parsed.chartCards) ? parsed.chartCards : [];
            const hiddenCardIds = Array.isArray(parsed.hiddenCardIds) ? parsed.hiddenCardIds : [];
            return { dataCards, chartCards, hiddenCardIds, _ts: parsed?._ts ?? null };
        } catch (e) {
            console.warn('[DashboardIndicators] No se pudo leer cache de order:', e);
            return null;
        }
    }, []);

    const readOrderCacheAnyUser = useCallback((activity) => {
        try {
            if (typeof window === 'undefined' || !window.localStorage) return null;
            if (!activity) return null;

            let best = null;

            for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i);
                if (!key) continue;

                if (!key.startsWith(`${ORDER_CACHE_PREFIX}:`)) continue;
                if (!key.endsWith(`:${activity}`)) continue;

                const raw = window.localStorage.getItem(key);
                if (!raw) continue;

                let parsed = null;
                try {
                    parsed = JSON.parse(raw);
                } catch {
                    continue;
                }

                const ts = parsed?._ts ?? 0;
                if (!best || ts > best._ts) {
                    best = parsed;
                }
            }

            if (!best || typeof best !== 'object') return null;

            const dataCards = Array.isArray(best.dataCards) ? best.dataCards : [];
            const chartCards = Array.isArray(best.chartCards) ? best.chartCards : [];
            const hiddenCardIds = Array.isArray(best.hiddenCardIds) ? best.hiddenCardIds : [];
            return { dataCards, chartCards, hiddenCardIds, _ts: best?._ts ?? null };
        } catch (e) {
            console.warn('[DashboardIndicators] No se pudo leer cache any-user de order:', e);
            return null;
        }
    }, []);

    const writeOrderCache = useCallback((userId, activity, orderObj) => {
        try {
            if (typeof window === 'undefined' || !window.localStorage) return;
            window.localStorage.setItem(
                getOrderCacheKey(userId, activity),
                JSON.stringify({ ...orderObj, _ts: Date.now() })
            );
        } catch (e) {
            console.warn('[DashboardIndicators] No se pudo guardar cache de order:', e);
        }
    }, []);

    const stripEntitySuffix = (title) =>
        typeof title === 'string' ? title.replace(/\s*\((Finca|Producto|Operador|Zafra|Lote|Tercio)\)$/, '') : title;

    const applyOrderToState = useCallback((activity, orderObj) => {
        const nextDataCards = Array.isArray(orderObj?.dataCards) ? orderObj.dataCards : [];
        const nextChartCards = (Array.isArray(orderObj?.chartCards) ? orderObj.chartCards : [])
            .map((c) => c ? { ...c, title: stripEntitySuffix(c.title) } : c);
        setOrderActivity(activity);
        setDataCardsOrder(nextDataCards);
        dataCardsOrderRef.current = nextDataCards;
        setChartCardsOrder(nextChartCards);
        chartCardsOrderRef.current = nextChartCards;

        // Restore hidden cards from the same order payload
        const savedHidden = Array.isArray(orderObj?.hiddenCardIds) ? orderObj.hiddenCardIds : [];
        const hiddenMap = {};
        savedHidden.forEach((id) => { hiddenMap[id] = true; });
        setHiddenCards(hiddenMap);
        hiddenCardsRef.current = hiddenMap;
    }, []);

    const hydrateOrderForActivity = useCallback((userId, activity) => {
        const mem = orderCacheRef.current?.[activity];
        if (mem && (Array.isArray(mem.dataCards) || Array.isArray(mem.chartCards))) return mem;

        const cached = readOrderCache(userId, activity);
        if (cached) orderCacheRef.current[activity] = cached;
        return cached;
    }, [readOrderCache]);

    /* ──── fetch card order from backend ──── */

    const fetchCardOrder = useCallback(async () => {
        if (!selectedActivity) return;

        const idUsuario = safeLoggedUserId ?? userData?.userId ?? null;

        let cached = null;
        if (idUsuario) {
            cached = hydrateOrderForActivity(idUsuario, selectedActivity);
        }

        if (!cached) {
            cached = readOrderCacheAnyUser(selectedActivity);
            if (cached) {
                orderCacheRef.current[selectedActivity] = cached;
            }
        }

        if (cached) {
            const normalizedCached = normalizeOrderForActivity(selectedActivity, cached);
            orderCacheRef.current[selectedActivity] = normalizedCached;
            if (idUsuario) writeOrderCache(idUsuario, selectedActivity, normalizedCached);
            applyOrderToState(selectedActivity, normalizedCached);
            const cachedHasCards = normalizedCached?.dataCards?.length > 0 || normalizedCached?.chartCards?.length > 0;
            if (idUsuario && cachedHasCards) {
                setLoadedOrderKey(getCardOrderReadyKey(idUsuario, selectedActivity));
            }
        } else {
            applyOrderToState(selectedActivity, { dataCards: [], chartCards: [] });
        }

        if (!idUsuario) return;

        const requestActivity = selectedActivity;
        const requestSeq = ++orderFetchSeqRef.current;

        setIsOrderLoading(true);

        try {
            const response = await axios.get(
                `${API_BASE_URL_DASHBOARD}api/indicators/order/${idUsuario}/${requestActivity}`
            );

            let rawOrder = response?.data?.order ?? null;

            // Mongoose [String] schema wraps a single string in an array — unwrap it
            if (Array.isArray(rawOrder) && rawOrder.length > 0) {
                rawOrder = rawOrder[0];
            }

            let parsedOrder = null;

            if (rawOrder && typeof rawOrder === 'string') {
                try {
                    parsedOrder = JSON.parse(rawOrder);
                } catch (e) {
                    console.error('[DashboardIndicators] No se pudo parsear `order` (string):', e, rawOrder);
                    parsedOrder = null;
                }
            } else if (rawOrder && typeof rawOrder === 'object') {
                parsedOrder = rawOrder;
            } else if (response?.data?.dataCards || response?.data?.chartCards) {
                parsedOrder = response.data;
            }

            let nextDataCards = Array.isArray(parsedOrder?.dataCards) ? parsedOrder.dataCards : [];
            let nextChartCards = Array.isArray(parsedOrder?.chartCards) ? parsedOrder.chartCards : [];
            const nextHiddenCardIds = Array.isArray(parsedOrder?.hiddenCardIds) ? parsedOrder.hiddenCardIds : [];

            // Inject any new cards that are not yet in the user's saved order
            const ACTIVITY_EXTRA_CARDS = {
                DETECCION_MALEZA: [
                    { id: 'totalUsd', title: 'Total de USD', type: 'dataCard', limitName: 'totalUsd', dimensional: 'USD' },
                ],
                APLICACIONES_AEREAS: [
                    { ...AA_DATA_CARD_DEFINITIONS.promedioPorcentajeVariacionDosis },
                    { ...AA_DATA_CARD_DEFINITIONS.promedioVolumenAgua },
                    { ...AA_DATA_CARD_DEFINITIONS.promedioDosisProducto },
                    { ...AA_DATA_CARD_DEFINITIONS.promedioVolumenMezcla },
                    { ...AA_DATA_CARD_DEFINITIONS.totalVolumenAgua },
                    { ...AA_DATA_CARD_DEFINITIONS.totalDosisProducto },
                    { ...AA_DATA_CARD_DEFINITIONS.totalVolumenMezcla },
                ],
            };

            if (requestActivity === 'APLICACIONES_AEREAS' && nextDataCards.length > 0) {
                nextDataCards = normalizeAADataCardsOrder(nextDataCards);
            }

            const extraCards = ACTIVITY_EXTRA_CARDS[requestActivity];
            if (extraCards && nextDataCards.length > 0) {
                const savedIds = new Set(nextDataCards.map((c) => c?.id));
                const missingCards = extraCards.filter((c) => !savedIds.has(c.id));
                if (missingCards.length > 0) {
                    nextDataCards = [...nextDataCards, ...missingCards];
                }
            }

            // Inject missing chart cards for new per-entity indicators (AA)
            const ACTIVITY_EXTRA_CHART_CARDS = {
                APLICACIONES_AEREAS: (() => {
                    const newIndicators = [
                        { id: 'VolumenAgua', title: 'Volumen Agua' },
                        { id: 'DosisProducto', title: 'Dosis Producto' },
                        { id: 'VolumenMezcla', title: 'Volumen Mezcla' },
                    ];
                    const entities = ['Finca', 'Producto', 'Operador', 'Zafra', 'Lote', 'Tercio'];
                    const cards = [];
                    for (const ind of newIndicators) {
                        for (const ent of entities) {
                            cards.push({ id: `promedio${ind.id}${ent}`, title: `Promedio ${ind.title}`, type: 'chartCard' }, { id: `total${ind.id}${ent}`, title: `Total ${ind.title}`, type: 'chartCard' });
                        }
                    }
                    // Per-Lote chart cards for existing indicators that may be missing from saved orders
                    cards.push({ id: 'promedioDosisTeoricaLote', title: 'Promedio Volumen Teórico', type: 'chartCard' }, { id: 'promedioHumedadLote', title: 'Promedio Humedad del Cultivo', type: 'chartCard' },{ id: 'promedioTchLote', title: 'Promedio TCH', type: 'chartCard' }, { id: 'totalTchLote', title: 'Total TCH', type: 'chartCard' }, { id: 'totalHumedadLote', title: 'Total Humedad del Cultivo', type: 'chartCard' });
                    return cards;
                })(),
            };
            const extraChartCards = ACTIVITY_EXTRA_CHART_CARDS[requestActivity];
            if (extraChartCards && nextChartCards.length > 0) {
                // Normalize IDs (strip spaces) when checking what's already saved, to avoid duplicating cards
                // that were previously stored with legacy space-containing IDs
                const savedChartIds = new Set(nextChartCards.map((c) => (c?.id ?? '').replace(/ /g, '')));
                const missingChartCards = extraChartCards.filter((c) => !savedChartIds.has(c.id));
                if (missingChartCards.length > 0) {
                    nextChartCards = [...nextChartCards, ...missingChartCards];
                }
            }

            const nextOrder = normalizeOrderForActivity(requestActivity, { dataCards: nextDataCards, chartCards: nextChartCards, hiddenCardIds: nextHiddenCardIds, _aaHiddenV: parsedOrder?._aaHiddenV });
            nextDataCards = nextOrder.dataCards;
            nextChartCards = nextOrder.chartCards;

            console.groupCollapsed?.(`[DashboardIndicators] card-order @ ${selectedActivity}`);
            console.log('userId:', idUsuario);
            console.log('orderType:', typeof rawOrder, rawOrder);
            console.log('dataCards:', nextDataCards.length, nextDataCards.map((c) => c?.id));
            console.log('chartCards:', nextChartCards.length, nextChartCards.map((c) => c?.id));
            console.log('hiddenCardIds:', nextHiddenCardIds.length);
            console.groupEnd?.();

            orderCacheRef.current[requestActivity] = nextOrder;
            writeOrderCache(idUsuario, requestActivity, nextOrder);

            if (requestActivity === selectedActivityRef.current && requestSeq === orderFetchSeqRef.current) {
                applyOrderToState(requestActivity, nextOrder);
                setLoadedOrderKey(getCardOrderReadyKey(idUsuario, requestActivity));
                fetchReasonRef.current = 'order';
                setFetchTriggerVersion((version) => version + 1);
                setShouldFetch(true);
            }
        } catch (error) {
            console.error('Error fetching card order:', error);
        } finally {
            if (requestSeq === orderFetchSeqRef.current) {
                setIsOrderLoading(false);
            }
        }
    }, [safeLoggedUserId, selectedActivity, userData?.userId, hydrateOrderForActivity, applyOrderToState, writeOrderCache, readOrderCacheAnyUser]);

    /* ──── debug effect (userData logging) ──── */
    useEffect(() => {
        // Debug de rol/usuario
    }, [userData, loggedUserId, isViewer, safeLoggedUserId, effectiveUserId, canEdit]);

    /* ──── card-order fetch trigger ──── */
    useEffect(() => {
        fetchCardOrder();
    }, [fetchCardOrder]);

    /* ──── prefetch card order ──── */
    const prefetchCardOrderForActivity = useCallback(async (activity) => {
        const idUsuario = safeLoggedUserId ?? userData?.userId ?? null;
        if (!idUsuario || !activity) return;

        const mem = orderCacheRef.current?.[activity];
        if (mem && (Array.isArray(mem.dataCards) || Array.isArray(mem.chartCards))) return;

        const cached = readOrderCache(idUsuario, activity);
        if (cached) {
            const normalizedCached = normalizeOrderForActivity(activity, cached);
            orderCacheRef.current[activity] = normalizedCached;
            writeOrderCache(idUsuario, activity, normalizedCached);
            return;
        }

        try {
            const response = await axios.get(
                `${API_BASE_URL_DASHBOARD}api/indicators/order/${idUsuario}/${activity}`
            );

            const rawOrder = response?.data?.order ?? response?.data ?? null;
            let parsedOrder = null;

            if (rawOrder && typeof rawOrder === 'string') {
                try {
                    parsedOrder = JSON.parse(rawOrder);
                } catch (e) {
                    parsedOrder = null;
                }
            } else if (rawOrder && typeof rawOrder === 'object') {
                parsedOrder = rawOrder;
            }

            const nextDataCards = Array.isArray(parsedOrder?.dataCards) ? parsedOrder.dataCards : [];
            const nextChartCards = Array.isArray(parsedOrder?.chartCards) ? parsedOrder.chartCards : [];
            const nextHiddenCardIds = Array.isArray(parsedOrder?.hiddenCardIds) ? parsedOrder.hiddenCardIds : [];

            const nextOrder = normalizeOrderForActivity(activity, { dataCards: nextDataCards, chartCards: nextChartCards, hiddenCardIds: nextHiddenCardIds, _aaHiddenV: parsedOrder?._aaHiddenV });
            orderCacheRef.current[activity] = nextOrder;
            writeOrderCache(idUsuario, activity, nextOrder);
        } catch (e) {
            console.warn('[DashboardIndicators] prefetch order falló:', activity, e);
        }
    }, [safeLoggedUserId, userData?.userId, readOrderCache, writeOrderCache]);

    useEffect(() => {
        const idUsuario = safeLoggedUserId ?? userData?.userId ?? null;
        if (!idUsuario) return;

        prefetchCardOrderForActivity('COSECHA_MECANICA');
        prefetchCardOrderForActivity('APLICACIONES_AEREAS');
    }, [safeLoggedUserId, userData?.userId, prefetchCardOrderForActivity]);

    useEffect(() => {
        if (!selectedActivity) return;

        const warmList = ['COSECHA_MECANICA', 'APLICACIONES_AEREAS'];
        warmList
            .filter((a) => a !== selectedActivity)
            .forEach((a) => {
                prefetchCardOrderForActivity(a);
            });
    }, [selectedActivity, prefetchCardOrderForActivity]);

    /* ═══════════════════════════════════════════════════ */
    /*           DRAG & DROP LOGIC                        */
    /* ═══════════════════════════════════════════════════ */

    const saveOrder = async (orderType, newOrder) => {
        try {
            if (!canEdit) {
                toast.info('Tu usuario es visualizador: no puedes modificar el orden.');
                return;
            }
            const idUsuario = safeLoggedUserId;
            if (!idUsuario) return;
            const preferencesOrder = {
                dataCards: orderType === 'dataCards' ? newOrder : dataCardsOrder,
                chartCards: orderType === 'chartCards' ? newOrder : chartCardsOrder,
                hiddenCardIds: Object.keys(hiddenCardsRef.current),
                _aaHiddenV: AA_HIDDEN_MIGRATION_VERSION,
            };

            await axios.post(`${API_BASE_URL_DASHBOARD}api/indicators/order`, {
                userId: idUsuario,
                analysis: selectedActivity,
                order: JSON.stringify(preferencesOrder),
            });

            // Update cache
            orderCacheRef.current[selectedActivity] = preferencesOrder;
            writeOrderCache(idUsuario, selectedActivity, preferencesOrder);

            if (orderType === 'dataCards') {
                setDataCardsOrder(newOrder);
                dataCardsOrderRef.current = newOrder;
            } else {
                setChartCardsOrder(newOrder);
                chartCardsOrderRef.current = newOrder;
            }
        } catch (error) {
            console.error('Error saving order:', error);
        }
    };

    const saveHiddenCards = useCallback(async (nextHiddenMap) => {
        setHiddenCards(nextHiddenMap);
        hiddenCardsRef.current = nextHiddenMap;

        // Re-fetch indicators so newly visible entity sections get their data loaded
        fetchReasonRef.current = 'visibility';
        setFetchTriggerVersion((version) => version + 1);
        setShouldFetch(true);

        const idUsuario = safeLoggedUserId;
        if (!idUsuario) return;

        const preferencesOrder = {
            dataCards: dataCardsOrder,
            chartCards: chartCardsOrder,
            hiddenCardIds: Object.keys(nextHiddenMap),
            _aaHiddenV: AA_HIDDEN_MIGRATION_VERSION,
        };

        // Update caches immediately
        orderCacheRef.current[selectedActivity] = preferencesOrder;
        writeOrderCache(idUsuario, selectedActivity, preferencesOrder);

        // Persist to backend
        try {
            await axios.post(`${API_BASE_URL_DASHBOARD}api/indicators/order`, {
                userId: idUsuario,
                analysis: selectedActivity,
                order: JSON.stringify(preferencesOrder),
            });
        } catch (error) {
            console.error('Error saving hidden cards:', error);
        }
    }, [safeLoggedUserId, selectedActivity, dataCardsOrder, chartCardsOrder, writeOrderCache]);

    const moveCardInGroup = (orderType, groupName, dragIndex, hoverIndex) => {
        const cardOrder = orderType === 'dataCards' ? dataCardsOrder : chartCardsOrder;
        const setCardOrder = orderType === 'dataCards' ? setDataCardsOrder : setChartCardsOrder;

        const subset = cardOrder.filter((c) => c._group === groupName);
        if (!subset[dragIndex]) return;
        const draggedCard = subset[dragIndex];

        const updatedSubset = [...subset];
        updatedSubset.splice(dragIndex, 1);
        updatedSubset.splice(hoverIndex, 0, draggedCard);

        const newOrder = [];
        let subsetIndex = 0;

        for (let i = 0; i < cardOrder.length; i++) {
            const card = cardOrder[i];
            if (card._group === groupName) {
                newOrder.push(updatedSubset[subsetIndex]);
                subsetIndex++;
            } else {
                newOrder.push(card);
            }
        }

        setCardOrder(newOrder);
        saveOrder(orderType, newOrder);
    };

    /* ═══════════════════════════════════════════════════ */
    /*           FILTERS / DATA LOADING                   */
    /* ═══════════════════════════════════════════════════ */

    const handleDateChange = useCallback((dates) => {
        setDateRange(dates);
    }, []);

    /* ──── populate filter bar based on activity ──── */
    useEffect(() => {
        let filters = [];
        filters.push({ label: 'Fecha Inicio', value: 'fechaInicio' });
        filters.push({ label: 'Fecha Fin', value: 'fechaFin' });

        if (selectedActivity === 'APLICACIONES_AEREAS') {
            filters.push({
                label: 'Tipo de Aeronave',
                value: 'tipoAeronave',
                options: ['Dron', 'Avioneta', 'Helicóptero'],
            });
            filters.push({ label: 'Operador', value: 'operadorAA' });
            filters.push({ label: 'Finca', value: 'fincaAA' });
            filters.push({ label: 'Producto', value: 'productoAA' });
            filters.push({
                label: 'Tipo de producto',
                value: 'tipoProductoAA',
                options: ['Líquido', 'Sólido'],
            });
            filters.push({ label: 'Zafra', value: 'zafraAA' });
            filters.push({ label: 'Lote', value: 'loteAA' });
            filters.push({ label: 'Tercio', value: 'tercioAA' });
        } else if (selectedActivity === 'COSECHA_MECANICA') {
            filters.push({ label: 'Zafra', value: 'zafraCM' });
            filters.push({ label: 'Tercio', value: 'tercioCM' });
            filters.push({ label: 'Finca', value: 'fincaCM' });
            filters.push({ label: 'Operador', value: 'operadorCM' });
            filters.push({ label: 'Lote', value: 'loteCM' });
        } else if (selectedActivity === 'FERTILIZACION') {
            filters.push({ label: 'Operador', value: 'operadorFR' });
            filters.push({ label: 'Finca', value: 'fincaFR' });
            filters.push({ label: 'Producto', value: 'productoFR' });
            filters.push({ label: 'Zafra', value: 'zafraFR' });
            filters.push({ label: 'Tercio', value: 'tercioFR' });
        } else if (selectedActivity === 'DETECCION_MALEZA') {
            filters.push({ label: 'Lote', value: 'loteDM' });
            filters.push({ label: 'Finca', value: 'fincaDM' });
            filters.push({ label: 'Zafra', value: 'zafraDM' });
            filters.push({ label: 'Tercio', value: 'tercioDM' });
        }

        setFilterOptions(filters);
    }, [selectedActivity]);

    /* ──── per-activity filter fetchers ──── */

    const fetchCosechaMecanicaFilters = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL_DASHBOARD}api/indicators/filters/cosecha-mecanica`,
                { params: { userId: safeLoggedUserId } }
            );
            setFiltersCM({
                loteOptions: response.data.lote || [],
                operadorOptions: response.data.operadores || [],
                fincasOptions: response.data.fincas || [],
                zafrasOptions: response.data.zafras || [],
                tercioOptions: response.data.tercios || [],
            });
            setFiltersFetched((prev) => ({ ...prev, COSECHA_MECANICA: true }));
        } catch (error) {
            console.error('Error fetching Cosecha Mecanica filters:', error);
        }
    }, [safeLoggedUserId]);

    const fetchAplicacionesAereasFilters = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL_DASHBOARD}api/indicators/filters/aplicaciones-aereas`,
                { params: { userId: safeLoggedUserId } }
            );
            setFiltersAA({
                operadorOptionsAA: response.data.responsables || [],
                fincasOptionsAA: response.data.fincas || [],
                productosOptionsAA: response.data.productos || [],
                zafraOptionsAA: response.data.zafras || [],
                loteOptionsAA: response.data.lotes || [],
                tercioOptionsAA: response.data.tercios || [],
            });
            setFiltersFetched((prev) => ({ ...prev, APLICACIONES_AEREAS: true }));
        } catch (error) {
            console.error('Error fetching Aplicaciones Aereas filters:', error);
        }
    }, [safeLoggedUserId]);

    const fetchFertilizacionFilters = useCallback(async () => {
        try {
            const { data } = await axios.get(
                `${API_BASE_URL_DASHBOARD}api/indicators/filters/fertilizacion`,
                { params: { userId: safeLoggedUserId } }
            );
            setFiltersFR({
                operadorOptionsFR: data.operadores || [],
                fincaOptionsFR: data.fincas || [],
                productosOptionsFR: data.productos || [],
                zafraOptionsFR: data.zafras || [],
                tercioOptionsFR: data.tercios || [],
            });
            setFiltersFetched((p) => ({ ...p, FERTILIZACION: true }));
        } catch (err) {
            console.error('Error fetching Fertilización filters:', err);
        }
    }, [safeLoggedUserId]);

    const fetchDeteccionMalezaFilters = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL_DASHBOARD}api/indicators/filters/deteccion-maleza`,
                { params: { userId: safeLoggedUserId } }
            );
            setFiltersDM({
                loteOptionsDM: response.data.lotes || [],
                fincasOptionsDM: response.data.fincas || [],
                zafraOptionsDM: response.data.zafras || [],
                tercioOptionsDM: response.data.tercios || [],
            });
            setFiltersFetched((prev) => ({ ...prev, DETECCION_MALEZA: true }));
        } catch (error) {
            console.error('Error fetching Detección Maleza filters:', error);
        }
    }, [safeLoggedUserId]);

    const fetchCompanyUsers = useCallback(async () => {
        if (!safeLoggedUserId) return;
        try {
            const response = await axios.get(
                `${API_BASE_URL_DASHBOARD}api/indicators/filters/company-users`,
                { params: { userId: safeLoggedUserId } }
            );
            setCompanyUsers(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching company users:', error);
        }
    }, [safeLoggedUserId]);

    useEffect(() => {
        fetchCompanyUsers();
    }, [fetchCompanyUsers]);

    useEffect(() => {
        if (!safeLoggedUserId) return; // wait for auth before fetching filters
        if (!filtersFetched[selectedActivity]) {
            if (selectedActivity === 'COSECHA_MECANICA') {
                fetchCosechaMecanicaFilters();
            } else if (selectedActivity === 'APLICACIONES_AEREAS') {
                fetchAplicacionesAereasFilters();
            } else if (selectedActivity === 'FERTILIZACION') {
                fetchFertilizacionFilters();
            } else if (selectedActivity === 'DETECCION_MALEZA') {
                fetchDeteccionMalezaFilters();
            }
        }
    }, [selectedActivity, filtersFetched, safeLoggedUserId, fetchCosechaMecanicaFilters, fetchAplicacionesAereasFilters, fetchFertilizacionFilters, fetchDeteccionMalezaFilters]);

    /* ──── activity change → reset ──── */
    useEffect(() => {
        console.log('[DashboardIndicators] selectedActivity changed ->', selectedActivity, '(limpiando estados)');
        setSelectedFilters({});
        setDateRange({
            startDate: moment().startOf('month'),
            endDate: moment().endOf('month'),
        });

        if (selectedActivity) {
            const idUsuario = safeLoggedUserId ?? userData?.userId ?? null;

            let cachedOrder = null;
            if (idUsuario) {
                cachedOrder = hydrateOrderForActivity(idUsuario, selectedActivity);
            }
            if (!cachedOrder) {
                cachedOrder = readOrderCacheAnyUser(selectedActivity);
            }

            if (cachedOrder) {
                applyOrderToState(selectedActivity, cachedOrder);
            } else {
                applyOrderToState(selectedActivity, { dataCards: [], chartCards: [] });
            }
        }
    }, [selectedActivity, resetIndicatorsData, safeLoggedUserId, userData?.userId, hydrateOrderForActivity, applyOrderToState, readOrderCacheAnyUser]);

    const handleActivitySelect = useCallback((activity) => {
        if (activity !== selectedActivity) {
            // Immediately cancel any in-flight requests
            if (abortControllerRef.current) abortControllerRef.current.abort();
            if (totalAnalisisAbortRef.current) totalAnalisisAbortRef.current.abort();
            fetchReasonRef.current = 'activity';
            setSelectedActivity(activity);
            setSelectedFilters({});
            setFetchTriggerVersion((version) => version + 1);
            setShouldFetch(true);
        }
    }, [selectedActivity]);

    const handleFilterClick = useCallback((filters) => {
        const { startDate, endDate } = dateRange;
        const diffInDays = endDate.diff(startDate, 'days');
        if (diffInDays > 365) {
            toast.warn('El rango de fechas no puede ser mayor a 365 días.');
        } else {
            // Cancel any in-flight requests before re-fetching
            if (abortControllerRef.current) abortControllerRef.current.abort();
            if (totalAnalisisAbortRef.current) totalAnalisisAbortRef.current.abort();
            setSelectedFilters(filters);
            setFetchTriggerVersion((version) => version + 1);
            setShouldFetch(true);
        }
    }, [dateRange]);

    /* ═══════════════════════════════════════════════════ */
    /*           FETCH TOTAL ANALISIS                     */
    /* ═══════════════════════════════════════════════════ */

    const fetchTotalAnalisis = useCallback(async () => {
        // Abort previous totalAnalisis request
        if (totalAnalisisAbortRef.current) {
            totalAnalisisAbortRef.current.abort();
        }
        const controller = new AbortController();
        totalAnalisisAbortRef.current = controller;
        const { signal } = controller;

        try {
            const { startDate, endDate } = dateRange;
            if (!startDate || !endDate || !effectiveUserId) return;

            const startDateFormatted = moment(startDate)
                .startOf('day')
                .format('DD/MM/YYYY');
            const endDateFormatted = moment(endDate)
                .endOf('day')
                .format('DD/MM/YYYY');

            const queryParams = new URLSearchParams({
                userId: String(effectiveUserId),
                FECHA_INICIO: startDateFormatted,
                FECHA_FINAL: endDateFormatted,
            });

            if (safeLoggedUserId) {
                queryParams.append('loggedUserId', String(safeLoggedUserId));
                queryParams.append('requestUserId', String(safeLoggedUserId));
                queryParams.append('safeLoggedUserId', String(safeLoggedUserId));
            }
            // Filtro por usuario de la empresa (solo cuando se selecciona explícitamente)
            if (selectedFilters.usuarioEmpresa) {
                const uid = companyUserEmailToId[selectedFilters.usuarioEmpresa];
                if (uid) queryParams.append('FILTER_USER_IDS', uid);
            }
            if (selectedActivity === 'COSECHA_MECANICA') {
                if (selectedFilters.fincaCM) queryParams.append('NOMBRE_FINCA', selectedFilters.fincaCM);
                if (selectedFilters.zafraCM) queryParams.append('ZAFRA', selectedFilters.zafraCM);
                if (selectedFilters.tercioCM) queryParams.append('TERCIO', selectedFilters.tercioCM);
                if (selectedFilters.operadorCM) queryParams.append('OPERADOR', selectedFilters.operadorCM);
                if (selectedFilters.loteCM) queryParams.append('LOTE', selectedFilters.loteCM);

                const resp = await axios.get(
                    `${API_BASE_URL_DASHBOARD}api/indicators/cosecha-mecanica/total-analisis?${queryParams}`,
                    { signal }
                );
                setTotalAnalisisCount(resp.data.totalAnalisis || 0);
            } else if (selectedActivity === 'APLICACIONES_AEREAS') {
                if (selectedFilters.fincaAA) queryParams.append('NOMBRE_FINCA', selectedFilters.fincaAA);
                if (selectedFilters.operadorAA) queryParams.append('RESPONSABLE', selectedFilters.operadorAA);
                if (selectedFilters.productoAA) queryParams.append('PRODUCTO', selectedFilters.productoAA);
                if (selectedFilters.zafraAA) queryParams.append('ZAFRA', selectedFilters.zafraAA);
                if (selectedFilters.loteAA) queryParams.append('LOTE', selectedFilters.loteAA);
                if (selectedFilters.tercioAA) queryParams.append('TERCIO', selectedFilters.tercioAA);
                if (selectedFilters.tipoAeronave) queryParams.append('EQUIPO', selectedFilters.tipoAeronave);
                if (selectedFilters.tipoProductoAA) queryParams.append('FORMULACION', selectedFilters.tipoProductoAA);

                const resp = await axios.get(
                    `${API_BASE_URL_DASHBOARD}api/indicators/aplicaciones-aereas/total-analisis?${queryParams}`,
                    { signal }
                );
                setTotalAnalisisCount(resp.data.totalAnalisis || 0);
            } else if (selectedActivity === 'FERTILIZACION') {
                if (selectedFilters.fincaFR) queryParams.append('NOMBRE_FINCA', selectedFilters.fincaFR);
                if (selectedFilters.operadorFR) queryParams.append('NOMBRE_DE_OPERADOR', selectedFilters.operadorFR);
                if (selectedFilters.productoFR) queryParams.append('PRODUCTO', selectedFilters.productoFR);
                if (selectedFilters.zafraFR) queryParams.append('ZAFRA', selectedFilters.zafraFR);
                if (selectedFilters.tercioFR) queryParams.append('TERCIO', selectedFilters.tercioFR);

                const resp = await axios.get(
                    `${API_BASE_URL_DASHBOARD}api/indicators/fertilizacion/total-analisis?${queryParams}`,
                    { signal }
                );
                setTotalAnalisisCount(resp.data.totalAnalisis || 0);
            } else if (selectedActivity === 'DETECCION_MALEZA') {
                if (selectedFilters.loteDM) queryParams.append('LOTE', selectedFilters.loteDM);
                if (selectedFilters.fincaDM) queryParams.append('NOMBRE_FINCA', selectedFilters.fincaDM);
                if (selectedFilters.zafraDM) queryParams.append('ZAFRA', selectedFilters.zafraDM);
                if (selectedFilters.tercioDM) queryParams.append('TERCIO', selectedFilters.tercioDM);

                const resp = await axios.get(
                    `${API_BASE_URL_DASHBOARD}api/indicators/deteccion-maleza/total-analisis?${queryParams}`,
                    { signal }
                );
                setTotalAnalisisCount(resp.data.totalAnalisis || 0);
            } else {
                setTotalAnalisisCount(0);
            }
        } catch (error) {
            if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
            setTotalAnalisisCount(0);
            console.error('Error al obtener total de análisis:', error);
        }
    }, [selectedActivity, dateRange, selectedFilters, effectiveUserId, safeLoggedUserId, companyUserEmailToId, isAnalyst]);

    /* ═══════════════════════════════════════════════════ */
    /*           FETCH INDICATORS                         */
    /* ═══════════════════════════════════════════════════ */

    const fetchIndicators = useCallback(async () => {
        // Abort any in-flight request so the new one starts immediately
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const { signal } = controller;

        const config = analysisConfig[selectedActivity];
        if (!config?.fetchDataIndicators || !dateRange.startDate || !dateRange.endDate) return;
        let didFetch = false;
        let currentFetchKey = '';

        try {

            const startKey = moment(dateRange.startDate).startOf('day').format('YYYY-MM-DD');
            const endKey = moment(dateRange.endDate).endOf('day').format('YYYY-MM-DD');
            const normalizedFilters = Object.keys(selectedFilters || {})
                .sort()
                .reduce((acc, k) => {
                    acc[k] = selectedFilters[k];
                    return acc;
                }, {});
            currentFetchKey = `${selectedActivity}|${startKey}|${endKey}|${effectiveUserId ?? 'unknown'}|${JSON.stringify(normalizedFilters)}`;

            const lastInfo = lastIndicatorsFetchRef.current[selectedActivity];
            const now = Date.now();
            const MIN_REFETCH_MS = 90 * 1000;

            if (
                fetchReasonRef.current === 'activity' &&
                lastInfo?.key === currentFetchKey &&
                typeof lastInfo?.ts === 'number' &&
                now - lastInfo.ts < MIN_REFETCH_MS
            ) {
                setShouldFetch(false);
                return;
            }

            didFetch = true;
            setLoading(true);

            const startDateFormatted = moment(dateRange.startDate)
                .startOf('day')
                .format('DD/MM/YYYY');
            const endDateFormatted = moment(dateRange.endDate)
                .endOf('day')
                .format('DD/MM/YYYY');
            const idUsuario = effectiveUserId;
            if (!idUsuario) {
                setLoading(false);
                return;
            }

            const { data: { limites: lims } } = await axios.get(
                `${API_BASE_URL}configuration/limites/${selectedActivity.toLowerCase()}`,
                { signal }
            );
            const fetchedLimites = lims.reduce((acc, l) => {
                acc[l.indicador] = { min: l.min, max: l.max };
                return acc;
            }, {});
            setLimites(fetchedLimites);

            const minMaxParams = { min: fetchedLimites, max: fetchedLimites };

            // Resolve user company filter ID only when explicitly selected
            const filterUserIdValue = selectedFilters.usuarioEmpresa
                ? companyUserEmailToId[selectedFilters.usuarioEmpresa]
                : null;
            const selectedFiltersWithIds = filterUserIdValue
                ? { ...selectedFilters, FILTER_USER_IDS: filterUserIdValue }
                : selectedFilters;

            /* ── Selective fetch: check which entity groups have visible chart cards ── */
            const hidden = hiddenCardsRef.current;
            const allCharts = chartCardsOrderRef.current;
            const hasChartOrder = allCharts.length > 0;
            const entityHasVisible = (suffix) =>
                !hasChartOrder || allCharts.some((c) => c.id.includes(suffix) && !hidden[c.id]);
            const allDataCardsHidden = dataCardsOrderRef.current.length > 0 &&
                dataCardsOrderRef.current.every((c) => hidden[c.id]);

            /* ---------- COSECHA MECÁNICA ---------- */
            if (selectedActivity === 'COSECHA_MECANICA') {
                const needMaquina = entityHasVisible('Maquina');
                const needOperador = entityHasVisible('Operador');
                const needFinca = entityHasVisible('Finca');
                const fetch = (ep) => config.fetchDataIndicators(startDateFormatted, endDateFormatted, ep, idUsuario, minMaxParams, selectedFiltersWithIds, { signal });
                const skip = Promise.resolve([]);

                const [
                    promedioResult,
                    porcentajesResult,
                    metricasMaquinaResult,
                    porcentajesMaquinaResult,
                    metricasOperadorResultCM,
                    porcentajesOperadorResultCM,
                    metricasFincaResult,
                    porcentajesFincaResult,
                ] = await Promise.all([
                    allDataCardsHidden ? skip : fetch('promedio-indicadores'),
                    allDataCardsHidden ? skip : fetch('porcentajes'),
                    needMaquina ? fetch('metricas-maquina') : skip,
                    needMaquina ? fetch('porcentajes-maquina') : skip,
                    needOperador ? fetch('metricas-operador') : skip,
                    needOperador ? fetch('porcentajes-operador') : skip,
                    needFinca ? fetch('metricas-finca') : skip,
                    needFinca ? fetch('porcentajes-finca') : skip,
                ]);

                setPromedioIndicadores(promedioResult);
                setPorcentajes(porcentajesResult);
                setMetricasMaquina(metricasMaquinaResult);
                setPorcentajesMaquina(porcentajesMaquinaResult);
                setMetricasOperadorCM(metricasOperadorResultCM);
                setPorcentajesOperadorCM(porcentajesOperadorResultCM);
                setMetricasFinca(metricasFincaResult);
                setPorcentajesFinca(porcentajesFincaResult);

            /* ---------- APLICACIONES AÉREAS ---------- */
            } else if (selectedActivity === 'APLICACIONES_AEREAS') {
                const selectedAircraft = selectedFiltersWithIds.tipoAeronave || '';
                const selectedFiltersWithAuth = {
                    ...selectedFiltersWithIds,
                    loggedUserId: safeLoggedUserId,
                    requestUserId: safeLoggedUserId,
                    safeLoggedUserId: safeLoggedUserId,
                };

                const needFinca = entityHasVisible('Finca');
                const needOperador = entityHasVisible('Operador');
                const needProducto = entityHasVisible('Producto');
                const needZafra = entityHasVisible('Zafra');
                const needLote = entityHasVisible('Lote');
                const needTercio = entityHasVisible('Tercio');
                const fetchAA = (ep) => config.fetchDataIndicators(startDateFormatted, endDateFormatted, ep, idUsuario, minMaxParams, selectedAircraft, selectedFiltersWithAuth, { signal });
                const skip = Promise.resolve([]);

                // Raw fetch for per-flight data (response is not {valor,titulo} format)
                const fetchAAVueloRaw = async () => {
                    try {
                        const vueloParams = new URLSearchParams();
                        vueloParams.set('FECHA_INICIO', startDateFormatted);
                        vueloParams.set('FECHA_FINAL', endDateFormatted);
                        vueloParams.set('userId', idUsuario ?? '');
                        vueloParams.set('EQUIPO', selectedAircraft || '');
                        vueloParams.set('FORMULACION', selectedFiltersWithAuth.tipoProductoAA || '');
                        vueloParams.set('RESPONSABLE', selectedFiltersWithAuth.operadorAA || '');
                        vueloParams.set('NOMBRE_FINCA', selectedFiltersWithAuth.fincaAA || '');
                        vueloParams.set('PRODUCTO', selectedFiltersWithAuth.productoAA || '');
                        vueloParams.set('ZAFRA', selectedFiltersWithAuth.zafraAA || '');
                        vueloParams.set('TERCIO', selectedFiltersWithAuth.tercioAA || '');
                        vueloParams.set('LOTE', selectedFiltersWithAuth.loteAA || '');
                        vueloParams.set('FILTER_USER_IDS', selectedFiltersWithAuth.FILTER_USER_IDS || '');
                        if (selectedFiltersWithAuth.loggedUserId) {
                            vueloParams.set('loggedUserId', selectedFiltersWithAuth.loggedUserId);
                            vueloParams.set('requestUserId', selectedFiltersWithAuth.loggedUserId);
                            vueloParams.set('safeLoggedUserId', selectedFiltersWithAuth.loggedUserId);
                        }
                        const url = `${API_BASE_URL_DASHBOARD}api/indicators/aplicaciones-aereas/metricas-vuelo?${vueloParams.toString()}`;
                        const resp = await fetch(url, { signal });
                        if (!resp.ok) return null;
                        return await resp.json();
                    } catch { return null; }
                };

                // Datos exclusivos VRA para el Dashboard Indicadores. Se consultan
                // adicionalmente y no reemplazan los endpoints APS convencionales.
                const buildAAVraParams = () => {
                    const vraParams = new URLSearchParams();
                    vraParams.set('FECHA_INICIO', startDateFormatted);
                    vraParams.set('FECHA_FINAL', endDateFormatted);
                    vraParams.set('userId', idUsuario ?? '');
                    vraParams.set('EQUIPO', selectedAircraft || '');
                    vraParams.set('FORMULACION', selectedFiltersWithAuth.tipoProductoAA || '');
                    vraParams.set('RESPONSABLE', selectedFiltersWithAuth.operadorAA || '');
                    vraParams.set('NOMBRE_FINCA', selectedFiltersWithAuth.fincaAA || '');
                    vraParams.set('PRODUCTO', selectedFiltersWithAuth.productoAA || '');
                    vraParams.set('ZAFRA', selectedFiltersWithAuth.zafraAA || '');
                    vraParams.set('TERCIO', selectedFiltersWithAuth.tercioAA || '');
                    vraParams.set('LOTE', selectedFiltersWithAuth.loteAA || '');
                    vraParams.set('FILTER_USER_IDS', selectedFiltersWithAuth.FILTER_USER_IDS || '');
                    if (selectedFiltersWithAuth.loggedUserId) {
                        vraParams.set('loggedUserId', selectedFiltersWithAuth.loggedUserId);
                        vraParams.set('requestUserId', selectedFiltersWithAuth.loggedUserId);
                        vraParams.set('safeLoggedUserId', selectedFiltersWithAuth.loggedUserId);
                    }
                    return vraParams;
                };

                const fetchAAVraRaw = async (endpoint) => {
                    try {
                        const url = `${API_BASE_URL_DASHBOARD}api/indicators/aplicaciones-aereas/${endpoint}?${buildAAVraParams().toString()}`;
                        const resp = await fetch(url, { signal });
                        if (!resp.ok) return null;
                        return await resp.json();
                    } catch { return null; }
                };

                const [
                    promedioResult,
                    metricasFincaResult,
                    tiempoTotalResult,
                    dosisTeoricaRealResult,
                    totalesIndicadoresResult,
                    totalesIndicadoresFincaResult,
                    totalesIndicadoresProductoResult,
                    metricasProductoResult,
                    totalesIndicadoresOperadorResult,
                    metricasOperadorResult,
                    totalesIndicadoresZafraResult,
                    metricasZafraResult,
                    totalesIndicadoresLoteResult,
                    metricasLoteResult,
                    totalesIndicadoresTercioResult,
                    metricasTercioResult,
                    metricasVueloResult,
                    vraResumenResult,
                    vraMetricasDimensionesResult,
                ] = await Promise.all([
                    allDataCardsHidden ? skip : fetchAA('promedio-indicadores'),
                    needFinca ? fetchAA('metricas-finca') : skip,
                    allDataCardsHidden ? skip : fetchAA('tiempo-total'),
                    allDataCardsHidden ? skip : fetchAA('teorica-real'),
                    allDataCardsHidden ? skip : fetchAA('totales-indicadores'),
                    needFinca ? fetchAA('totales-indicadores-finca') : skip,
                    needProducto ? fetchAA('totales-indicadores-producto') : skip,
                    needProducto ? fetchAA('metricas-producto') : skip,
                    needOperador ? fetchAA('totales-indicadores-operador') : skip,
                    needOperador ? fetchAA('metricas-operador') : skip,
                    needZafra ? fetchAA('totales-indicadores-zafra') : skip,
                    needZafra ? fetchAA('metricas-zafra') : skip,
                    needLote ? fetchAA('totales-indicadores-lote') : skip,
                    needLote ? fetchAA('metricas-lote') : skip,
                    needTercio ? fetchAA('totales-indicadores-tercio') : skip,
                    needTercio ? fetchAA('metricas-tercio') : skip,
                    fetchAAVueloRaw(),
                    fetchAAVraRaw('vra-resumen'),
                    fetchAAVraRaw('vra-metricas-dimensiones'),
                ]);

                setPromedioIndicadores(promedioResult || []);
                setMetricasFinca(metricasFincaResult || []);
                setTiempoTotal(tiempoTotalResult || []);
                setDosisTeoricaReal(dosisTeoricaRealResult || []);
                setTotalesIndicadores(totalesIndicadoresResult || []);

                setTotalesIndicadoresFinca(
                    (totalesIndicadoresFincaResult || []).map(i => ({
                        ...i,
                        NOMBRE_FINCA: i._id ?? null,
                        _id: undefined,
                    })),
                );

                setTotalesIndicadoresProducto(totalesIndicadoresProductoResult || []);
                setMetricasProducto(metricasProductoResult || []);
                setTotalesIndicadoresOperador(totalesIndicadoresOperadorResult || []);
                setMetricasOperador(metricasOperadorResult || []);
                setTotalesIndicadoresZafra(totalesIndicadoresZafraResult || []);
                setMetricasZafra(metricasZafraResult || []);
                setTotalesIndicadoresLote(totalesIndicadoresLoteResult || []);
                setMetricasLote(metricasLoteResult || []);
                setTotalesIndicadoresTercio(totalesIndicadoresTercioResult || []);
                setMetricasTercio(metricasTercioResult || []);
                setMetricasVuelo(metricasVueloResult || null);
                setVraResumen(vraResumenResult || null);
                setVraMetricasDimensiones(vraMetricasDimensionesResult || {});

            /* ---------- FERTILIZACIÓN ---------- */
            } else if (selectedActivity === 'FERTILIZACION') {
                const needFinca = entityHasVisible('Finca');
                const needProducto = entityHasVisible('Producto');
                const needOperador = entityHasVisible('Operador');
                const needZafra = entityHasVisible('Zafra');
                const fetchFR = (ep) => config.fetchDataIndicators(startDateFormatted, endDateFormatted, ep, idUsuario, minMaxParams, selectedFiltersWithIds, { signal });
                const skip = Promise.resolve([]);

                const [
                    totalAnalisisResult,
                    promedioResult,
                    totalesIndicadoresResult,
                    metricasFincaResult,
                    totalesIndicadoresFincaResult,
                    metricasProductoResult,
                    totalesIndicadoresProductoResult,
                    metricasOperadorResult,
                    totalesIndicadoresOperadorResult,
                    totalesIndicadoresZafraResult,
                    metricasZafraResult,
                ] = await Promise.all([
                    axios.get(
                        `${API_BASE_URL_DASHBOARD}api/indicators/fertilizacion/total-analisis?` +
                        `FECHA_INICIO=${startDateFormatted}&FECHA_FINAL=${endDateFormatted}&userId=${idUsuario}`,
                        { signal }
                    ),
                    allDataCardsHidden ? skip : fetchFR('promedio-indicadores'),
                    allDataCardsHidden ? skip : fetchFR('totales-indicadores'),
                    needFinca ? fetchFR('metricas-finca') : skip,
                    needFinca ? fetchFR('totales-indicadores-finca') : skip,
                    needProducto ? fetchFR('metricas-producto') : skip,
                    needProducto ? fetchFR('totales-indicadores-producto') : skip,
                    needOperador ? fetchFR('metricas-operador') : skip,
                    needOperador ? fetchFR('totales-indicadores-operador') : skip,
                    needZafra ? fetchFR('totales-indicadores-zafra') : skip,
                    needZafra ? fetchFR('metricas-zafra') : skip,
                ]);

                setTotalAnalisisCount(totalAnalisisResult.data.totalAnalisis || 0);
                setPromedioIndicadores(promedioResult || []);
                setTotalesIndicadores(totalesIndicadoresResult || []);
                setMetricasFinca(metricasFincaResult || []);
                setTotalesIndicadoresFinca(
                    (totalesIndicadoresFincaResult || []).map(i => ({
                        ...i,
                        NOMBRE_FINCA: i._id ?? null,
                        _id: undefined,
                    })),
                );
                setMetricasProducto(metricasProductoResult || []);
                setTotalesIndicadoresProducto(totalesIndicadoresProductoResult || []);
                setMetricasOperador(metricasOperadorResult || []);
                setTotalesIndicadoresOperador(totalesIndicadoresOperadorResult || []);
                setTotalesIndicadoresZafra(totalesIndicadoresZafraResult || []);
                setMetricasZafra(metricasZafraResult || []);

            /* ---------- DETECCIÓN DE MALEZA ---------- */
            } else if (selectedActivity === 'DETECCION_MALEZA') {
                const needFinca = entityHasVisible('Finca');
                const needZafra = entityHasVisible('Zafra');
                const needTercio = entityHasVisible('Tercio');
                const fetchDM = (ep) => config.fetchDataIndicators(startDateFormatted, endDateFormatted, ep, idUsuario, minMaxParams, selectedFiltersWithIds, { signal });
                const skip = Promise.resolve([]);

                const [
                    totalesIndicadoresResult,
                    metricasFincaResult,
                    totalesIndicadoresFincaResult,
                    metricasZafraResult,
                    totalesIndicadoresZafraResult,
                    metricasTercioResult,
                    totalesIndicadoresTercioResult,
                ] = await Promise.all([
                    allDataCardsHidden ? skip : fetchDM('totales-indicadores'),
                    needFinca ? fetchDM('metricas-finca') : skip,
                    needFinca ? fetchDM('totales-indicadores-finca') : skip,
                    needZafra ? fetchDM('metricas-zafra') : skip,
                    needZafra ? fetchDM('totales-indicadores-zafra') : skip,
                    needTercio ? fetchDM('metricas-tercio') : skip,
                    needTercio ? fetchDM('totales-indicadores-tercio') : skip,
                ]);

                setPromedioIndicadores([]);
                setMetricasFincaDM(metricasFincaResult || []);
                setTotalesIndicadoresFincaDM(
                    (totalesIndicadoresFincaResult || []).map(i => {
                        const mappedItem = {
                            ...i,
                            NOMBRE_FINCA: i._id,
                        };
                        Object.keys(i).forEach(key => {
                            if (i[key] && typeof i[key] === 'object' && i[key].valor !== undefined) {
                                mappedItem[key] = {
                                    ...i[key],
                                    value: i[key].valor
                                };
                            }
                        });
                        return mappedItem;
                    }),
                );
                setMetricasZafraDM(metricasZafraResult || []);
                setTotalesIndicadoresZafraDM(totalesIndicadoresZafraResult || []);
                setMetricasTercioDM(metricasTercioResult || []);
                setTotalesIndicadoresTercioDM(totalesIndicadoresTercioResult || []);

                // Datos por lote desde BD para la tabla
                let mappedLotes = [];
                try {
                    const queryParamsLotes = new URLSearchParams({
                        userId: idUsuario,
                        FECHA_INICIO: startDateFormatted,
                        FECHA_FINAL: endDateFormatted,
                    });

                    if (selectedFilters.loteDM) queryParamsLotes.append('LOTE', selectedFilters.loteDM);
                    if (selectedFilters.fincaDM) queryParamsLotes.append('NOMBRE_FINCA', selectedFilters.fincaDM);
                    if (selectedFilters.zafraDM) queryParamsLotes.append('ZAFRA', selectedFilters.zafraDM);
                    if (selectedFilters.tercioDM) queryParamsLotes.append('TERCIO', selectedFilters.tercioDM);
                    if (filterUserIdValue) queryParamsLotes.append('FILTER_USER_IDS', filterUserIdValue);

                    const respLotes = await axios.get(
                        `${API_BASE_URL_DASHBOARD}api/indicators/deteccion-maleza/lotes?${queryParamsLotes}`,
                        { signal }
                    );

                    const lotesRaw = respLotes.data || [];

                    mappedLotes = (Array.isArray(lotesRaw) ? lotesRaw : []).map(item => {
                        if (!item) return null;
                        if (typeof item === 'string') {
                            return {
                                nombre_finca: '',
                                productor: '',
                                id_numerico: '',
                                zona: '',
                                lote_id: item,
                                codigo_lote: item,
                                area_lote_ha: 0,
                                area_maleza_ha: 0,
                                porcentaje_maleza: 0,
                            };
                        }

                        return {
                            nombre_finca: item.nombre_finca || item.NOMBRE_FINCA || item.finca || '',
                            productor: item.productor || item.PRODUCTOR || '',
                            id_numerico: item.id_numerico || item.ID_NUMERICO || item.ID || item.id || '',
                            zona: item.zona || item.ZONA || '',
                            lote_id: item.lote_id || item.LOTE || item.codigo_lote || item._id || '',
                            codigo_lote: item.codigo_lote || item.CODIGO_LOTE || item.lote_id || item._id || '',
                            area_lote_ha: Number(item.area_lote_ha ?? item.area_lote ?? item.area_poligono ?? 0),
                            area_maleza_ha: Number(item.area_maleza_ha ?? item.area_maleza ?? 0),
                            porcentaje_maleza: Number(item.porcentaje_maleza ?? item.porcentaje ?? 0),
                            precio_usd_ha: (
                                item.precio_usd_ha !== undefined && item.precio_usd_ha !== null
                            ) ? Number(item.precio_usd_ha) : (
                                item.PRECIO_USD_HA !== undefined && item.PRECIO_USD_HA !== null
                            ) ? Number(item.PRECIO_USD_HA) : (
                                item.precio !== undefined && item.precio !== null
                            ) ? Number(item.precio) : null,
                        };
                    }).filter(Boolean);

                    setLotesDMData(mappedLotes);
                } catch (err) {
                    console.error('Error fetching lotes DM:', err);
                    setLotesDMData([]);
                }

                // Compute Total de USD from lotes data (area_maleza_ha × precio_usd_ha)
                const totalUsd = mappedLotes.reduce((sum, lot) => {
                    const usd = (lot.area_maleza_ha || 0) * (lot.precio_usd_ha || 0);
                    return sum + usd;
                }, 0);

                // Inject totalUsd into totalesIndicadores
                const augmentedTotales = (totalesIndicadoresResult || []).length > 0
                    ? totalesIndicadoresResult.map((item, idx) =>
                        idx === 0
                            ? { ...item, totalUsd: { title: 'Total de USD', value: totalUsd } }
                            : item
                    )
                    : [{ totalUsd: { title: 'Total de USD', value: totalUsd } }];

                setTotalesIndicadores(augmentedTotales);
            }

            if (didFetch && currentFetchKey) {
                lastIndicatorsFetchRef.current[selectedActivity] = { ts: Date.now(), key: currentFetchKey };
            }

            setShouldFetch(false);
        } catch (err) {
            // Ignore AbortError — it means we intentionally cancelled
            if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
                console.log('[fetchIndicators] Aborted (activity/filter changed)');
                return;
            }
            console.error('Error fetching indicators:', err);
        } finally {
            fetchReasonRef.current = 'init';
            setIsRefreshing(false);
            setLoading(false);
            setShouldFetch(false);
        }
    }, [selectedActivity, dateRange.startDate, dateRange.endDate, selectedFilters, effectiveUserId, companyUserEmailToId, safeLoggedUserId, isAnalyst]);

    useEffect(() => {
        const idUsuario = safeLoggedUserId ?? userData?.userId ?? null;
        const currentOrderKey = idUsuario && selectedActivity
            ? getCardOrderReadyKey(idUsuario, selectedActivity)
            : null;
        const hasUnhandledFetchTrigger = handledFetchTriggerRef.current !== fetchTriggerVersion;

        if (currentOrderKey && loadedOrderKey === currentOrderKey && (shouldFetch || hasUnhandledFetchTrigger)) {
            handledFetchTriggerRef.current = fetchTriggerVersion;
            fetchIndicators();
            fetchTotalAnalisis();
        }
    }, [shouldFetch, fetchTriggerVersion, loadedOrderKey, selectedActivity, safeLoggedUserId, userData?.userId, fetchIndicators, fetchTotalAnalisis]);

    // Cleanup: abort all in-flight requests on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) abortControllerRef.current.abort();
            if (totalAnalisisAbortRef.current) totalAnalisisAbortRef.current.abort();
        };
    }, []);

    /* ═══════════════════════════════════════════════════ */
    /*           HELPERS / DERIVED DATA                   */
    /* ═══════════════════════════════════════════════════ */

    const formatValue = (value) =>
        typeof value === 'number'
            ? value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : value;

    const getMinMaxFromLimits = useCallback((card) => {
        const key = card.limitName || card.id;
        const limit = limites[key] || { min: null, max: null };
        return {
            min: limit.min !== null ? limit.min : 'N/A',
            max: limit.max !== null ? limit.max : 'N/A',
        };
    }, [limites]);

    const isAplicacionesAereas = selectedActivity === 'APLICACIONES_AEREAS';
    const isFertilizacion = selectedActivity === 'FERTILIZACION';
    const isDeteccionMaleza = selectedActivity === 'DETECCION_MALEZA';
    const isCosechaMecanica = selectedActivity === 'COSECHA_MECANICA';

    /* ──── area chart data builders ──── */

    const buildDistribucionAreasData = useCallback((isPromedio = true) => {
        const ids = isPromedio
            ? AREA_INDICATORS.promedio.donut
            : AREA_INDICATORS.total.donut;

        const [aplicada, sobre, noAplicada] = ids.map((id) =>
            promedioIndicadores[0]?.[id]?.value ??
            totalesIndicadores[0]?.[id]?.value ??
            0
        );

        return [
            { name: 'aplicada', value: round2(aplicada) },
            { name: 'sobre aplicada', value: round2(sobre) },
            { name: 'no aplicada', value: round2(noAplicada) },
        ];
    }, [promedioIndicadores, totalesIndicadores]);

    const buildAreaNoProductivaData = useCallback((isPromedio = true) => {
        const ids = isPromedio
            ? AREA_INDICATORS.promedio.bar
            : AREA_INDICATORS.total.bar;

        const [fuera, calles] = ids.map((id) =>
            promedioIndicadores[0]?.[id]?.value ??
            totalesIndicadores[0]?.[id]?.value ??
            0
        );

        return [
            { name: 'Fuera del lote', value: round2(fuera) },
            { name: 'En calles', value: round2(calles) },
        ];
    }, [promedioIndicadores, totalesIndicadores]);

    const distAreasPromData = useMemo(
        () => buildDistribucionAreasData(true),
        [buildDistribucionAreasData]
    );
    const areaNoProdPromData = useMemo(
        () => buildAreaNoProductivaData(true),
        [buildAreaNoProductivaData]
    );
    const distAreasTotalData = useMemo(
        () => buildDistribucionAreasData(false),
        [buildDistribucionAreasData]
    );
    const areaNoProdTotalData = useMemo(
        () => buildAreaNoProductivaData(false),
        [buildAreaNoProductivaData]
    );

    /* ──── card grouping ──── */

    const CM_SECTION_MAP = {
        velocidad: 'VelocidadRPM', rpm: 'VelocidadRPM',
        porcentajeEngagedPilotoAutomatico: 'PilotoAutomatico', porcentajeDisengagedPilotoAutomatico: 'PilotoAutomatico',
        porcentajeEngagedAutoTracket: 'PilotoAutomatico', porcentajeDisengagedAutoTracket: 'PilotoAutomatico',
        presionCorteBase: 'CorteBase', porcentajeAutomaticoModoCorteBase: 'CorteBase', porcentajeManualModoCorteBase: 'CorteBase',
        calidadGps: 'IndicadoresVarios', consumoCombustible: 'IndicadoresVarios', tch: 'IndicadoresVarios', tah: 'IndicadoresVarios',
    };

    const getDataCardCategory = (cardId) => {
        if (isCosechaMecanica && CM_SECTION_MAP[cardId]) {
            return CM_SECTION_MAP[cardId];
        }
        const idLower = cardId.toLowerCase();
        if (idLower === 'tch' || idLower === 'promediotch') {
            return isAplicacionesAereas ? 'IndicadoresVarios' : 'Indicadores';
        }

        if (VARIOS_CARD_IDS.includes(cardId) || idLower.includes('tch')) {
            return 'IndicadoresVarios';
        }
        if (idLower.includes('dosis')) return 'Dosis';
        if (idLower.includes('total')) return 'Totales';
        if (idLower.includes('promedio')) return 'Promedios';
        return 'Indicadores';
    };

    const groupDataCardsByCategory = useCallback((cardsArray) => {
        const groups = {
            Totales: [], Promedios: [], Dosis: [],
            VelocidadRPM: [], PilotoAutomatico: [], CorteBase: [],
            IndicadoresVarios: [],
            IndicadoresGenerales: [],
            Indicadores: [],
        };

        const areaIds = new Set([
            ...AREA_INDICATORS.promedio.donut,
            ...AREA_INDICATORS.promedio.bar,
            ...AREA_INDICATORS.total.donut,
            ...AREA_INDICATORS.total.bar,
        ]);

        cardsArray.forEach((card) => {
            if (
                AREA_INDICATORS.promedio.donut.includes(card.id) ||
                AREA_INDICATORS.promedio.bar.includes(card.id) ||
                AREA_INDICATORS.total.donut.includes(card.id) ||
                AREA_INDICATORS.total.bar.includes(card.id)
            ) return;

            if ((isAplicacionesAereas || isFertilizacion || isDeteccionMaleza) && areaIds.has(card.id)) return;

            const category = getDataCardCategory(card.id);
            card._group = category;
            groups[category].push(card);
        });

        return groups;
    }, [isAplicacionesAereas, isFertilizacion, isDeteccionMaleza, isCosechaMecanica]);

    const getChartCardCategory = (cardId) => {
        // Per-flight line charts get their own category
        const PER_VUELO_IDS = ['alturasPorVuelo', 'litrosPorVuelo', 'litrosHaPorVuelo'];
        if (PER_VUELO_IDS.includes(cardId)) return 'Gráficas por Vuelo';

        let entity = '';

        if (cardId.includes('Operador')) {
            entity = 'Operador';
        } else if (cardId.includes('Finca')) {
            entity = 'Finca';
        } else if (cardId.includes('Maquina')) {
            entity = 'Máquina';
        } else if (cardId.includes('Zafra')) {
            entity = 'Zafra';
        } else if (cardId.includes('Tercio')) {
            entity = 'Tercio';
        } else if (cardId.includes('Producto')) {
            entity = 'Producto';
        } else if (cardId.includes('Lote')) {
            entity = 'Lote';
        } else {
            entity = 'General';
        }

        if (isCosechaMecanica && entity !== 'General') {
            return `Gráficas por ${entity}`;
        }

        if (cardId.startsWith('promedio') || cardId.startsWith('diferencia')) {
            return `Promedios por ${entity}`;
        } else if (cardId.startsWith('total')) {
            return `Totales por ${entity}`;
        }
        return 'Indicadores';
    };

    const groupChartCardsByCategory = useCallback((cardsArray) => {
        const grouped = {};

        cardsArray.forEach((card) => {
            const category = getChartCardCategory(card.id);
            card._group = category;

            if (!grouped[category]) grouped[category] = [];
            grouped[category].push(card);
        });

        return grouped;
    }, [isCosechaMecanica]);

    /* ──── chart data mapping ──── */

    const mapChartDataForTotalsFinca = useCallback((data, cardId) => {
        return data
            .map((item) => {
                const fincaName = item.NOMBRE_FINCA || item._id || 'Sin Finca';

                if (item[cardId] && item[cardId].value !== undefined) {
                    const raw = Number(item[cardId].value);
                    const rounded = Number(Math.round(raw * 1000) / 1000);
                    return { name: fincaName, value: rounded };
                } else if (item[cardId] && item[cardId].valor !== undefined) {
                    const raw = Number(item[cardId].valor);
                    const rounded = Number(Math.round(raw * 1000) / 1000);
                    return { name: fincaName, value: rounded };
                } else {
                    const propertyKey = Object.keys(item).find(key =>
                        key.endsWith(cardId.split('total')[1]) &&
                        item[key] &&
                        (item[key].value !== undefined || item[key].valor !== undefined)
                    );

                    if (propertyKey) {
                        const val = item[propertyKey].value !== undefined ?
                            item[propertyKey].value : item[propertyKey].valor;
                        const raw = Number(val);
                        const rounded = Number(Math.round(raw * 1000) / 1000);
                        return { name: fincaName, value: rounded };
                    }
                }
                return null;
            })
            .filter(Boolean)
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }, []);

    const mapChartDataForTotalsGeneric = useCallback((data, cardId, nameKey) => {
        return data
            .map((item) => {
                if (item[cardId]) {
                    let displayName = item[nameKey] || 'Sin datos';

                    if (selectedActivity === 'DETECCION_MALEZA' && nameKey === '_id') {
                        if (/^\d{2}-\d{2}$/.test(displayName)) {
                            displayName = `Zafra ${displayName}`;
                        } else if (/^\d+$/.test(displayName)) {
                            displayName = `Tercio ${displayName}`;
                        }
                    }

                    const raw = item[cardId].value !== undefined ? Number(item[cardId].value) : null;
                    const rounded = raw !== null ? Number(Math.round(raw * 1000) / 1000) : null;
                    return { name: displayName, value: rounded };
                }
                return null;
            })
            .filter(Boolean)
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }, [selectedActivity]);

    const mapChartData = useCallback((metrics, percentages, cardId) => {
        const combinedData = [];

        metrics.forEach((metric) => {
            if (metric[cardId]) {
                let displayName = metric._id || 'Sin datos';

                if (selectedActivity === 'DETECCION_MALEZA' && metric._id) {
                    if (/^\d{2}-\d{2}$/.test(metric._id)) {
                        displayName = `Zafra ${metric._id}`;
                    } else if (/^\d+$/.test(metric._id)) {
                        displayName = `Tercio ${metric._id}`;
                    }
                }

                const raw = metric[cardId]?.value !== undefined ? Number(metric[cardId].value) : null;
                const rounded = raw !== null ? Number(Math.round(raw * 1000) / 1000) : null;
                combinedData.push({ name: displayName, value: rounded });
            }
        });

        percentages.forEach((percentage) => {
            if (percentage[cardId]) {
                let displayName = percentage._id || 'Sin datos';

                if (selectedActivity === 'DETECCION_MALEZA' && percentage._id) {
                    if (/^\d{2}-\d{2}$/.test(percentage._id)) {
                        displayName = `Zafra ${percentage._id}`;
                    } else if (/^\d+$/.test(percentage._id)) {
                        displayName = `Tercio ${percentage._id}`;
                    }
                }

                combinedData.push({
                    name: displayName,
                    value: percentage[cardId]?.value ?? null,
                });
            }
        });

        return combinedData
            .filter((item) => item.value !== null && item.value !== undefined)
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }, [selectedActivity]);

    /* ──── value lookup helper (for dataCards) ──── */

    const lookupCardValue = useCallback((cardId) => {
        return (
            promedioIndicadores[0]?.[cardId]?.value ??
            porcentajes[0]?.[cardId]?.value ??
            metricasMaquina[0]?.[cardId]?.value ??
            porcentajesMaquina[0]?.[cardId]?.value ??
            metricasOperadorCM[0]?.[cardId]?.value ??
            porcentajesOperadorCM[0]?.[cardId]?.value ??
            metricasFinca[0]?.[cardId]?.value ??
            porcentajesFinca[0]?.[cardId]?.value ??
            tiempoTotal[0]?.[cardId]?.value ??
            dosisTeoricaReal[0]?.[cardId]?.value ??
            totalesIndicadores[0]?.[cardId]?.value ??
            totalesIndicadoresFinca[0]?.[cardId]?.value ??
            totalesIndicadoresProducto[0]?.[cardId]?.value ??
            metricasProducto[0]?.[cardId]?.value ??
            totalesIndicadoresOperador[0]?.[cardId]?.value ??
            metricasOperador[0]?.[cardId]?.value ??
            totalesIndicadoresZafra[0]?.[cardId]?.value ??
            metricasZafra[0]?.[cardId]?.value ??
            totalesIndicadoresLote[0]?.[cardId]?.value ??
            metricasLote[0]?.[cardId]?.value ??
            totalesIndicadoresTercio[0]?.[cardId]?.value ??
            metricasTercio[0]?.[cardId]?.value ??
            metricasFincaDM[0]?.[cardId]?.value ??
            totalesIndicadoresFincaDM[0]?.[cardId]?.value ??
            metricasZafraDM[0]?.[cardId]?.value ??
            totalesIndicadoresZafraDM[0]?.[cardId]?.value ??
            metricasTercioDM[0]?.[cardId]?.value ??
            totalesIndicadoresTercioDM[0]?.[cardId]?.value
        );
    }, [
        promedioIndicadores, porcentajes, metricasMaquina, porcentajesMaquina,
        metricasOperadorCM, porcentajesOperadorCM, metricasFinca, porcentajesFinca,
        tiempoTotal, dosisTeoricaReal, totalesIndicadores,
        totalesIndicadoresFinca, totalesIndicadoresProducto, metricasProducto,
        totalesIndicadoresOperador, metricasOperador,
        totalesIndicadoresZafra, metricasZafra,
        totalesIndicadoresLote, metricasLote,
        totalesIndicadoresTercio, metricasTercio,
        metricasFincaDM, totalesIndicadoresFincaDM,
        metricasZafraDM, totalesIndicadoresZafraDM,
        metricasTercioDM, totalesIndicadoresTercioDM,
    ]);

    /* ──── chart data lookup (for chartCards) ──── */

    const lookupChartData = useCallback((cardId) => {
        // Per-flight line charts
        const PER_VUELO_MAP = {
            alturasPorVuelo: 'alturas',
            litrosPorVuelo: 'dosisReales',
            litrosHaPorVuelo: 'dosisAreas',
        };
        if (PER_VUELO_MAP[cardId] && metricasVuelo) {
            const arr = metricasVuelo[PER_VUELO_MAP[cardId]];
            if (Array.isArray(arr) && arr.length > 0) {
                return arr.map((v, i) => ({ name: String(i + 1), value: v ?? 0 }));
            }
            return [];
        }

        let chartData = mapChartData(metricasMaquina, porcentajesMaquina, cardId);
        if (!chartData.length) chartData = mapChartData(metricasOperadorCM, porcentajesOperadorCM, cardId);
        if (!chartData.length) chartData = mapChartData(metricasFinca, porcentajesFinca, cardId);
        if (!chartData.length) chartData = mapChartData(metricasProducto, [], cardId);
        if (!chartData.length) chartData = mapChartData(metricasOperador, [], cardId);
        if (!chartData.length) chartData = mapChartData(metricasZafra, [], cardId);
        if (!chartData.length) chartData = mapChartData(metricasLote, [], cardId);
        if (!chartData.length) chartData = mapChartData(metricasTercio, [], cardId);
        if (!chartData.length) chartData = mapChartData(metricasFincaDM, [], cardId);
        if (!chartData.length) chartData = mapChartData(metricasZafraDM, [], cardId);
        if (!chartData.length) chartData = mapChartData(metricasTercioDM, [], cardId);
        if (!chartData.length && totalesIndicadoresFinca.length) chartData = mapChartDataForTotalsFinca(totalesIndicadoresFinca, cardId);
        if (!chartData.length && totalesIndicadoresProducto.length) chartData = mapChartDataForTotalsGeneric(totalesIndicadoresProducto, cardId, '_id');
        if (!chartData.length && totalesIndicadoresOperador.length) chartData = mapChartDataForTotalsGeneric(totalesIndicadoresOperador, cardId, '_id');
        if (!chartData.length && totalesIndicadoresZafra.length) chartData = mapChartDataForTotalsGeneric(totalesIndicadoresZafra, cardId, '_id');
        if (!chartData.length && totalesIndicadoresLote.length) chartData = mapChartDataForTotalsGeneric(totalesIndicadoresLote, cardId, '_id');
        if (!chartData.length && totalesIndicadoresTercio.length) chartData = mapChartDataForTotalsGeneric(totalesIndicadoresTercio, cardId, '_id');
        if (!chartData.length && totalesIndicadoresFincaDM.length) chartData = mapChartDataForTotalsFinca(totalesIndicadoresFincaDM, cardId);
        if (!chartData.length && totalesIndicadoresZafraDM.length) chartData = mapChartDataForTotalsGeneric(totalesIndicadoresZafraDM, cardId, '_id');
        if (!chartData.length && totalesIndicadoresTercioDM.length) chartData = mapChartDataForTotalsGeneric(totalesIndicadoresTercioDM, cardId, '_id');
        return chartData;
    }, [
        mapChartData, mapChartDataForTotalsFinca, mapChartDataForTotalsGeneric,
        metricasMaquina, porcentajesMaquina, metricasOperadorCM, porcentajesOperadorCM,
        metricasFinca, porcentajesFinca, metricasProducto, metricasOperador,
        metricasZafra, metricasLote, metricasTercio,
        metricasFincaDM, metricasZafraDM, metricasTercioDM,
        totalesIndicadoresFinca, totalesIndicadoresProducto, totalesIndicadoresOperador,
        totalesIndicadoresZafra, totalesIndicadoresLote, totalesIndicadoresTercio,
        totalesIndicadoresFincaDM, totalesIndicadoresZafraDM, totalesIndicadoresTercioDM,
        metricasVuelo,
    ]);

    /* ═══════════════════════════════════════════════════ */
    /*           RETURN                                   */
    /* ═══════════════════════════════════════════════════ */

    return {
        // Auth / user
        userData,
        safeLoggedUserId,
        isViewer,
        canEdit,
        effectiveUserId,

        // Activity
        selectedActivity,
        handleActivitySelect,
        isAplicacionesAereas,
        isFertilizacion,
        isDeteccionMaleza,
        isCosechaMecanica,

        // Filters
        filterOptions,
        filtersCM,
        filtersAA,
        filtersFR,
        filtersDM,
        companyUsers,
        selectedFilters,
        dateRange,
        handleDateChange,
        handleFilterClick,

        // Loading
        loading,
        isRefreshing,
        isOrderLoading,
        showGlobalLoader,

        // Card order
        dataCardsOrder,
        chartCardsOrder,
        orderActivity,
        moveCardInGroup,

        // Card visibility
        hiddenCards,
        saveHiddenCards,

        // Data arrays
        promedioIndicadores,
        porcentajes,
        metricasMaquina,
        porcentajesMaquina,
        metricasOperadorCM,
        porcentajesOperadorCM,
        metricasFinca,
        porcentajesFinca,
        tiempoTotal,
        dosisTeoricaReal,
        totalesIndicadores,
        totalesIndicadoresFinca,
        totalesIndicadoresProducto,
        metricasProducto,
        totalesIndicadoresOperador,
        metricasOperador,
        totalesIndicadoresZafra,
        metricasZafra,
        totalesIndicadoresLote,
        metricasLote,
        totalesIndicadoresTercio,
        metricasTercio,
        metricasVuelo,
        vraResumen,
        vraMetricasDimensiones,
        metricasFincaDM,
        totalesIndicadoresFincaDM,
        metricasZafraDM,
        totalesIndicadoresZafraDM,
        metricasTercioDM,
        totalesIndicadoresTercioDM,
        lotesDMData,
        totalAnalisisCount,
        limites,

        // Derived area data
        distAreasPromData,
        areaNoProdPromData,
        distAreasTotalData,
        areaNoProdTotalData,

        // Helpers
        formatValue,
        getMinMaxFromLimits,
        lookupCardValue,
        lookupChartData,
        groupDataCardsByCategory,
        groupChartCardsByCategory,
        mapChartData,
        mapChartDataForTotalsFinca,
        mapChartDataForTotalsGeneric,

        // Misc
        setShouldFetch,
    };
}
